import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api } from '../services/api';
import './ReportsPage.css';

/**
 * Reports (ruling, Odin 2026-10-21): judge WAW by clicks to play and next-day
 * return, not email opens. Read live from D1 each time the tab opens, through
 * GET /api/reports, which only reads. The day is Central time; only on-day
 * play counts. Raw counts always show: the page is private, so the
 * 50-finisher rule does not apply. No emails are shown.
 */

type View = 'yesterday' | 'week' | 'month';

interface ReportDay {
  date: string;
  word: string | null;
  started: number;
  finished: number;
  first: number;
  second: number;
  third: number;
  missed: number;
  new_players: number;
  returned: number;
  return_pct: number | null;
  return_final: boolean;
  new_subscribers: number;
  share_visits: Record<string, number>;
  share_visits_total: number;
}

interface ReportTotals extends Omit<ReportDay, 'date' | 'word'> {
  from: string | null;
  to: string | null;
  days: number;
}

interface Report {
  view: View;
  today: string;
  days: ReportDay[];
  totals: ReportTotals;
  previous?: ReportTotals;
}

const VIEWS: { id: View; label: string }[] = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'Last 30 days' },
];

// share_visits.source tags, as the public worker writes them.
const SOURCES: Record<string, string> = {
  nl: 'Newsletter', ig: 'Instagram', tt: 'TikTok', fb: 'Facebook', pin: 'Pinterest', x: 'X',
  w1: 'Card: 1st try', w2: 'Card: 2nd try', w3: 'Card: 3rd try', l: 'Card: missed',
  dare: 'Dare', rw: 'Related Word', direct: 'Direct',
};
const SOURCE_ORDER = Object.keys(SOURCES);

const sourceLabel = (s: string) => SOURCES[s] ?? s;
const sortSources = (entries: [string, number][]) =>
  entries.sort((a, b) => b[1] - a[1] || SOURCE_ORDER.indexOf(a[0]) - SOURCE_ORDER.indexOf(b[0]));

function shortDate(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function returnText(pct: number | null): string {
  return pct === null ? 'n/a' : `${pct}%`;
}

function Delta({ now, before, suffix = '' }: { now: number | null; before: number | null; suffix?: string }) {
  if (now === null || before === null) return null;
  const diff = now - before;
  const cls = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const sign = diff > 0 ? '+' : '';
  return <span className={`reports-delta ${cls}`}>{sign}{diff}{suffix} vs the 7 before ({before}{suffix})</span>;
}

function Summary({ t, prev }: { t: ReportTotals; prev?: ReportTotals }) {
  const tiles: { label: string; value: string; note?: string; delta?: JSX.Element | null }[] = [
    {
      label: 'Next-day return',
      value: returnText(t.return_pct),
      note: `${t.returned} of ${t.finished} finishers played the next day${t.return_final ? '' : ' (today still counting)'}`,
      delta: prev ? <Delta now={t.return_pct} before={prev.return_pct} suffix=" pts" /> : null,
    },
    { label: 'Started', value: String(t.started), delta: prev ? <Delta now={t.started} before={prev.started} /> : null },
    { label: 'Finished', value: String(t.finished), delta: prev ? <Delta now={t.finished} before={prev.finished} /> : null },
    { label: 'New players', value: String(t.new_players), delta: prev ? <Delta now={t.new_players} before={prev.new_players} /> : null },
    { label: 'Share visits', value: String(t.share_visits_total), delta: prev ? <Delta now={t.share_visits_total} before={prev.share_visits_total} /> : null },
    { label: 'New subscribers', value: String(t.new_subscribers), delta: prev ? <Delta now={t.new_subscribers} before={prev.new_subscribers} /> : null },
  ];
  const sources = sortSources(Object.entries(t.share_visits));
  return (
    <>
      <div className="reports-tiles">
        {tiles.map((tile) => (
          <div key={tile.label} className="reports-tile">
            <div className="reports-tile-label">{tile.label}</div>
            <div className="reports-tile-value">{tile.value}</div>
            {tile.note && <div className="reports-tile-note">{tile.note}</div>}
            {tile.delta}
          </div>
        ))}
      </div>
      <div className="reports-strip">
        <span className="reports-strip-label">Split</span>
        <span>1st {t.first}</span><span>2nd {t.second}</span><span>3rd {t.third}</span><span>Missed {t.missed}</span>
        <span className="reports-strip-label">Visits by source</span>
        {sources.length === 0 ? <span className="reports-muted">none</span>
          : sources.map(([s, n]) => <span key={s}>{sourceLabel(s)} {n}</span>)}
      </div>
    </>
  );
}

export default function ReportsPage() {
  const [view, setView] = useState<View>('yesterday');
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (v: View) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await api.get(`/api/reports?view=${v}`) as Report);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(view); }, [view, load]);

  const days = report && report.view === view ? [...report.days].reverse() : [];

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <div className="reports-title">Reports</div>
          <div className="reports-subtitle">
            Clicks to play and next-day return, read live from the game tables. Days run midnight to midnight Central.
            Only on-day play counts; archive plays never do.
          </div>
        </div>
        <div className="reports-views" role="tablist">
          {VIEWS.map((v) => (
            <button key={v.id} role="tab" aria-selected={view === v.id}
              className={`reports-view ${view === v.id ? 'active' : ''}`} onClick={() => setView(v.id)}>
              {v.label}
            </button>
          ))}
          <button className="reports-refresh" onClick={() => load(view)} disabled={loading}>Refresh</button>
        </div>
      </div>

      {error && <div className="reports-error">{error}</div>}
      {loading && !report && <div className="reports-muted">Loading...</div>}

      {report && report.view === view && (
        <div className="reports-body">
          {report.totals.from && (
            <div className="reports-range">
              {report.totals.from === report.totals.to ? shortDate(report.totals.from) : `${shortDate(report.totals.from)} to ${shortDate(report.totals.to!)}`}
              {report.previous?.from && ` · compared with ${shortDate(report.previous.from)} to ${shortDate(report.previous.to!)}`}
            </div>
          )}
          <Summary t={report.totals} prev={report.previous} />

          <div className="reports-table-wrap">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Day</th><th>Word</th><th>Started</th><th>Finished</th>
                  <th>1st</th><th>2nd</th><th>3rd</th><th>Missed</th>
                  <th>New players</th><th>Next-day return</th><th>New subscribers</th><th>Share visits by source</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.date}>
                    <td className="reports-nowrap">{shortDate(d.date)}</td>
                    <td>{d.word ?? <span className="reports-muted">no game</span>}</td>
                    <td>{d.started}</td>
                    <td>{d.finished}</td>
                    <td>{d.first}</td>
                    <td>{d.second}</td>
                    <td>{d.third}</td>
                    <td>{d.missed}</td>
                    <td>{d.new_players}</td>
                    <td className="reports-nowrap">
                      <strong>{returnText(d.return_pct)}</strong>{' '}
                      <span className="reports-muted">{d.returned} of {d.finished}{d.return_final ? '' : ', so far'}</span>
                    </td>
                    <td>{d.new_subscribers}</td>
                    <td>
                      {d.share_visits_total === 0 ? <span className="reports-muted">0</span>
                        : sortSources(Object.entries(d.share_visits)).map(([s, n]) => (
                          <span key={s} className="reports-chip">{sourceLabel(s)} {n}</span>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
